import { cn } from '@sohanemon/utils';
import { Motion } from '@sohanscript/ui/motion';
import {
	animate,
	useMotionValue,
	useReducedMotion,
	useTransform,
} from 'motion/react';
import * as React from 'react';
import cursorSvg from '../../../styles/src/cursors/default.svg?raw';

const cursorMask = `url("data:image/svg+xml,${encodeURIComponent(
	cursorSvg.replaceAll('__FILL__', 'white').replaceAll('__STROKE__', 'white'),
)}")`;

const DURATION = 2.2;
const REST = { x: 18, y: 16 };
// NOTE: outer tip of default.svg, measured from a 256px rsvg render (bbox 41,18 → /8).
const HOTSPOT = { x: 5.125, y: 2.25 };

type Ease = (v: number) => number;
const linear: Ease = (v) => v;
const easeOut: Ease = (v) => 1 - (1 - v) ** 3;
const easeInOut: Ease = (v) =>
	v < 0.5 ? 4 * v ** 3 : 1 - (-2 * v + 2) ** 3 / 2;
const easeOutBack: Ease = (v) => {
	const c1 = 1.70158;
	const c3 = c1 + 1;
	return 1 + c3 * (v - 1) ** 3 + c1 * (v - 1) ** 2;
};

function sample(v: number, times: number[], values: number[], eases: Ease[]) {
	for (let i = 0; i < times.length - 1; i++) {
		if (v <= times[i + 1]) {
			const span = times[i + 1] - times[i];
			const local = span === 0 ? 1 : (v - times[i]) / span;
			return values[i] + (values[i + 1] - values[i]) * eases[i](local);
		}
	}
	return values[values.length - 1];
}

// NOTE: cursor glides in, anticipates (lifts), strikes, overshoots on release, holds, exits.
const CURSOR_TIMES = [0, 0.32, 0.42, 0.46, 0.52, 0.62, 1];
const GLIDE_EASE: Ease[] = [
	easeInOut,
	easeOut,
	linear,
	easeOut,
	easeOut,
	easeInOut,
];
const SCALE_EASE: Ease[] = [
	easeInOut,
	easeOut,
	linear,
	easeOutBack,
	easeOut,
	easeInOut,
];

// NOTE: crisp ring fires at impact, soft echo trails it, dot flashes at the hotspot.
const RING_TIMES = [0, 0.46, 0.48, 0.78, 1];
const RING_EASE: Ease[] = [linear, linear, easeOut, linear];
const ECHO_TIMES = [0, 0.5, 0.53, 0.88, 1];
const ECHO_EASE: Ease[] = [linear, linear, easeOut, linear];
const FLASH_TIMES = [0, 0.44, 0.47, 0.58, 1];
const FLASH_EASE: Ease[] = [linear, linear, easeOut, linear];

type ClickMockProps = React.ComponentProps<'div'>;

export function ClickMock({ className, ...props }: ClickMockProps) {
	const reduce = useReducedMotion();
	const t = useMotionValue(0);

	React.useEffect(() => {
		if (reduce) return;
		const controls = animate(t, 1, {
			duration: DURATION,
			ease: 'linear',
			repeat: Infinity,
			repeatDelay: 0.4,
		});
		return () => controls.stop();
	}, [reduce, t]);

	const x = useTransform(t, (v) =>
		sample(v, CURSOR_TIMES, [REST.x, 0, 0, 0, 0, 0, REST.x], GLIDE_EASE),
	);
	const y = useTransform(t, (v) =>
		sample(v, CURSOR_TIMES, [REST.y, 0, -3, 0, 0, 0, REST.y], GLIDE_EASE),
	);
	const scale = useTransform(t, (v) =>
		sample(v, CURSOR_TIMES, [1, 1, 0.94, 0.76, 1.07, 1, 1], SCALE_EASE),
	);
	const rotate = useTransform(t, (v) =>
		sample(v, CURSOR_TIMES, [10, 0, -6, 0, 0, 0, 10], GLIDE_EASE),
	);
	const shadow = useTransform(t, (v) =>
		sample(v, CURSOR_TIMES, [1, 1, 0.6, 0.4, 1, 1, 1], SCALE_EASE),
	);
	const filter = useTransform(
		shadow,
		(s) =>
			`drop-shadow(0 ${(2 * s).toFixed(2)}px ${(2 * s).toFixed(2)}px rgb(0 0 0 / ${(0.3 * s + 0.08).toFixed(3)}))`,
	);

	const ringOpacity = useTransform(t, (v) =>
		sample(v, RING_TIMES, [0, 0, 0.9, 0, 0], RING_EASE),
	);
	const ringScale = useTransform(t, (v) =>
		sample(v, RING_TIMES, [0.25, 0.25, 0.4, 1.25, 1.25], RING_EASE),
	);
	const echoOpacity = useTransform(t, (v) =>
		sample(v, ECHO_TIMES, [0, 0, 0.45, 0, 0], ECHO_EASE),
	);
	const echoScale = useTransform(t, (v) =>
		sample(v, ECHO_TIMES, [0.3, 0.3, 0.45, 1.7, 1.7], ECHO_EASE),
	);
	const flashOpacity = useTransform(t, (v) =>
		sample(v, FLASH_TIMES, [0, 0, 0.9, 0, 0], FLASH_EASE),
	);
	const flashScale = useTransform(t, (v) =>
		sample(v, FLASH_TIMES, [0.15, 0.15, 0.6, 1.1, 1.1], FLASH_EASE),
	);

	return (
		<div
			className={cn('pointer-events-none relative size-20', className)}
			aria-hidden="true"
			{...props}
		>
			<div className="absolute top-1/2 left-1/2 size-0">
				{!reduce && (
					<>
						<Motion
							as="span"
							animate={{}}
							className="absolute -top-7 -left-7 size-14 rounded-full border"
							style={{
								opacity: echoOpacity,
								scale: echoScale,
								borderColor: 'var(--preview-primary, var(--primary))',
							}}
						/>
						<Motion
							as="span"
							animate={{}}
							className="absolute -top-5 -left-5 size-10 rounded-full border-2"
							style={{
								opacity: ringOpacity,
								scale: ringScale,
								borderColor: 'var(--preview-primary, var(--primary))',
								backgroundColor:
									'color-mix(in oklab, var(--preview-primary, var(--primary)) 18%, transparent)',
							}}
						/>
						<Motion
							as="span"
							animate={{}}
							className="absolute -top-1 -left-1 size-2 rounded-full"
							style={{
								opacity: flashOpacity,
								scale: flashScale,
								backgroundColor: 'var(--preview-primary, var(--primary))',
							}}
						/>
					</>
				)}

				<Motion
					as="div"
					animate={{}}
					className="absolute size-8"
					style={{
						top: -HOTSPOT.y,
						left: -HOTSPOT.x,
						x: reduce ? 0 : x,
						y: reduce ? 0 : y,
						scale: reduce ? 1 : scale,
						rotate: reduce ? 0 : rotate,
						filter: reduce ? undefined : filter,
						transformOrigin: `${HOTSPOT.x}px ${HOTSPOT.y}px`,
						backgroundColor: 'var(--preview-primary, var(--primary))',
						maskImage: cursorMask,
						maskRepeat: 'no-repeat',
						maskSize: '100% 100%',
						WebkitMaskImage: cursorMask,
						WebkitMaskRepeat: 'no-repeat',
						WebkitMaskSize: '100% 100%',
					}}
				/>
			</div>
		</div>
	);
}
